import { Quote, ArrowRight, Check, Tags, Mic } from 'lucide-react';

// HowMatchingWorksPage — ticket 7.
//
// Route: /how-matching-works. This exact path is load-bearing — the author
// repo's description-field tooltip (AddBook.jsx) already builds
// `${VITE_READER_URL}/how-matching-works`, and the reader's own craving flow
// links here too. Get the path wrong and both "(i)" links 404 no matter how
// good this page is.
//
// Content is the policy doc verbatim (How_Matching_Works_Page.md), not a
// paraphrase — this page IS the explanation the tooltips promise, so the
// wording has to match what support/legal signed off on, not a rewrite of
// it. Every sentence below is unchanged from that doc; the worked-example
// cards quote the same phrases the paragraphs already use, word for word,
// they don't add new copy.

const NO_AI_POINTS = [
  'Nothing on Reanlo writes, rewrites, summarizes, or generates text about your book, your reviews, or your reflections.',
  'We never train any AI on your work, and we never sell it to anyone who would.',
  'Your words stay yours. You keep the copyright. We just get permission to host them and match them.',
];

export default function HowMatchingWorksPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-cream-page)', fontFamily: 'var(--font-body)', color: 'var(--brand-charcoal)' }}>
      <div style={{ padding: '14px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="/" style={{ color: 'var(--neutral-500)', textDecoration: 'none', fontSize: 'var(--font-size-md)', fontWeight: 500 }}>← Reanlo</a>
      </div>

      <header style={{ position: 'relative', overflow: 'hidden', padding: '56px 40px 48px', background: 'linear-gradient(180deg, var(--brand-cream) 0%, var(--brand-paper) 100%)', borderBottom: '1px solid var(--brand-sand)' }}>
        {/* Decorative — purely visual, no content */}
        <div aria-hidden="true" style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, var(--brand-gold-light) 0%, transparent 70%)', opacity: 0.5 }} />
        <div aria-hidden="true" style={{ position: 'absolute', bottom: -80, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, var(--brand-rust-light) 0%, transparent 70%)', opacity: 0.18 }} />

        <div style={{ position: 'relative', maxWidth: 680, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', marginBottom: 20, boxShadow: 'var(--shadow-sm)' }}>
            <Sparkle />
          </div>

          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5.5vw, 2.9rem)', fontWeight: 700, margin: '0 0 24px', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
            How matching works
          </h1>

          <div style={{ position: 'relative', background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderLeft: '4px solid var(--brand-rust)', borderRadius: 'var(--radius-lg)', padding: '20px 24px 20px 52px', boxShadow: 'var(--shadow-md)' }}>
            <Quote size={22} strokeWidth={2.5} color="var(--brand-rust)" style={{ position: 'absolute', left: 18, top: 18, opacity: 0.85 }} />
            <p style={{ fontSize: 'var(--font-size-lg)', color: 'var(--neutral-700)', margin: 0, lineHeight: 1.7, fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>
              "You said no Generative AI. So how does Reanlo understand what I wrote?"
            </p>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 680, margin: '0 auto', padding: '48px 40px 100px' }}>

        {/* ── How it works ─────────────────────────────────────────────── */}
        <section style={{ paddingBottom: 40, marginBottom: 40, borderBottom: '1px solid var(--brand-sand)' }}>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'var(--neutral-600)', margin: '0 0 16px' }}>
            Reanlo matches meaning, not tags. When an author describes their book, and a reader describes the book they're craving, Reanlo compares the meaning of those two pieces of writing and connects them when they're close. That's it. It's a mathematical comparison of words a human wrote, not a machine writing anything new.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'var(--neutral-600)', margin: '0 0 24px' }}>
            Here's what that looks like. An author describes their book as "a deeply moving story about grief, second chances, and choosing your own family." A reader comes in looking for "something with a second chance and found family." Reanlo connects them, because "choosing your own family" means nearly the same thing as "found family," and "second chances" means nearly the same thing as "second chance." Nothing got written. We just measured that the two people were talking about the same things.
          </p>

          {/* Worked example — same two quotes from the paragraph above, laid out
              so the match is seen, not just described. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 16, background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-xl)', padding: '22px 20px', boxShadow: 'var(--shadow-sm)' }}>
            <ExampleCard
              label="The author wrote"
              text={'"...a deeply moving story about grief, second chances, and choosing your own family."'}
              tone="forest"
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '50%', background: 'var(--brand-gold-light)', flexShrink: 0 }}>
              <ArrowRight size={18} strokeWidth={2.5} color="var(--brand-rust)" />
            </div>
            <ExampleCard
              label="The reader typed"
              text={'"...something with a second chance and found family."'}
              tone="rust"
            />
          </div>
        </section>

        {/* ── Tropes optional ───────────────────────────────────────────── */}
        <section style={{ paddingBottom: 40, marginBottom: 40, borderBottom: '1px solid var(--brand-sand)' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--brand-cream)', flexShrink: 0, marginTop: 2 }}>
              <Tags size={18} strokeWidth={2} color="var(--brand-rust)" />
            </div>
            <p style={{ fontSize: 15, lineHeight: 1.85, color: 'var(--neutral-600)', margin: 0 }}>
              This is why tropes are optional. Because Reanlo matches on meaning, your book can be found by a reader searching in trope language even if you never picked a single trope. In the example above, the author never tapped a "found family" tag, and the book still surfaced for the reader who wanted exactly that. The tropes shown on your book are only the ones you chose to add. We will never generate them for you.
            </p>
          </div>
        </section>

        {/* ── What "no generative AI" means ─────────────────────────────── */}
        <section style={{ paddingBottom: 40, marginBottom: 40, borderBottom: '1px solid var(--brand-sand)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, margin: '0 0 18px', lineHeight: 1.3 }}>
            What "no generative AI" actually means here
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {NO_AI_POINTS.map((point, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: 'var(--brand-forest)', flexShrink: 0, marginTop: 1 }}>
                  <Check size={14} strokeWidth={3} color="var(--brand-white)" />
                </div>
                <span style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--neutral-600)' }}>{point}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Give us real words ─────────────────────────────────────────── */}
        <section style={{ paddingBottom: 40, marginBottom: 40, borderBottom: '1px solid var(--brand-sand)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, margin: '0 0 14px', lineHeight: 1.3 }}>
            One thing we ask of you: give us real words
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.85, color: 'var(--neutral-600)', margin: '0 0 20px' }}>
            If a reader types only "Succession," there's almost nothing to match against, because one word barely carries any meaning to compare. A generative AI would just invent its own summary of what Succession is "about" and match on that. We don't do that. Instead we ask you to say what you actually liked: "Succession, the family politics, the money, everyone clawing for power, the buried secrets." Now there's real, human-written meaning to match, and we can connect it to a book whose description is about exactly that. The short minimum length is what keeps matching honest, without a machine inventing anything on anyone's behalf.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MiniCompareRow bad text="Succession" caption="almost nothing to match against" />
            <MiniCompareRow text="Succession, the family politics, the money, everyone clawing for power, the buried secrets." caption="real, human-written meaning" />
          </div>
        </section>

        {/* ── Voice note ─────────────────────────────────────────────────── */}
        <section style={{ paddingBottom: 40, marginBottom: 40, borderBottom: '1px solid var(--brand-sand)' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--brand-cream)', flexShrink: 0, marginTop: 2 }}>
              <Mic size={18} strokeWidth={2} color="var(--brand-rust)" />
            </div>
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, margin: '0 0 10px', lineHeight: 1.3 }}>
                And if you'd rather talk than type
              </h2>
              <p style={{ fontSize: 15, lineHeight: 1.85, color: 'var(--neutral-600)', margin: 0 }}>
                Some parts of Reanlo let you speak instead of type. When you do, your words are turned into text, and that's all we keep. We don't store a recording of your voice. Once it's transcribed, the audio is gone. And you can always choose to type instead.
              </p>
            </div>
          </div>
        </section>

        {/* ── Closing line ──────────────────────────────────────────────── */}
        <section style={{ background: 'var(--brand-forest)', borderRadius: 'var(--radius-xl)', padding: '32px 28px', textAlign: 'center', boxShadow: 'var(--shadow-md)' }}>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--brand-white)', fontWeight: 600, margin: 0, fontFamily: 'var(--font-display)' }}>
            That's the whole thing. Human words, matched to human words. No machine speaking for you.
          </p>
        </section>

      </main>
    </div>
  );
}

// ── Small presentational pieces ─────────────────────────────────────────────

function ExampleCard({ label, text, tone }) {
  const border = tone === 'forest' ? 'var(--brand-forest)' : 'var(--brand-rust)';
  return (
    <div style={{ background: 'var(--brand-cream)', border: `1px solid var(--brand-sand)`, borderTop: `3px solid ${border}`, borderRadius: 'var(--radius-md)', padding: '14px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: border, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--neutral-700)', fontStyle: 'italic' }}>
        {text}
      </div>
    </div>
  );
}

function MiniCompareRow({ bad, text, caption }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: '12px 16px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%',
        background: bad ? 'var(--feedback-error-bg)' : 'var(--feedback-success-bg)', flexShrink: 0,
      }}>
        <span style={{ color: bad ? 'var(--feedback-error)' : 'var(--feedback-success)', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
          {bad ? '×' : '✓'}
        </span>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: 'var(--neutral-700)', fontStyle: 'italic', lineHeight: 1.4 }}>"{text}"</div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', marginTop: 2 }}>{caption}</div>
      </div>
    </div>
  );
}

function Sparkle() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2l2.2 6.8L21 11l-6.8 2.2L12 20l-2.2-6.8L3 11l6.8-2.2L12 2z" fill="var(--brand-rust)" />
    </svg>
  );
}
